import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, CheckCircle, Circle, Info, ArrowSquareOut, Buildings, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { COUNTRY_FLAGS } from "@/data/compass";

/**
 * RequisitosLocales — dynamic local-compliance section.
 *
 * Renders per-country requirements returned by GET /api/compliance/requirements,
 * grouped into Pendientes vs. Completados. Never blocks progression.
 * When the API says `early=true`, we show the empty-state reason.
 *
 * Props:
 *   variant: "full" (default) — used inside PlaybookDetail
 *            "compact" — small pending-count card for Hoy
 *   onChange: () => void — callback after any update (so parent can refetch overview)
 */
export default function RequisitosLocales({ variant = "full", onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // per-req_id saving state
  const [showCompleted, setShowCompleted] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/compliance/requirements");
      setData(r.data);
    } catch {
      toast.error("No se pudieron cargar los requisitos locales");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    setSaving((s) => ({ ...s, [item.id]: true }));
    try {
      await api.put(`/compliance/requirements/${item.id}`, { completed: !item.completed });
      // Optimistically update local list
      setData((d) => ({
        ...d,
        items: d.items.map((it) =>
          it.id === item.id ? { ...it, completed: !it.completed } : it
        ),
      }));
      if (!item.completed) {
        toast.success(`Requisito completado: ${item.title}`);
      }
      onChange?.();
    } catch {
      toast.error("No se pudo actualizar el requisito");
    } finally {
      setSaving((s) => ({ ...s, [item.id]: false }));
    }
  };

  if (loading && variant === "compact") return null;
  if (loading) {
    return (
      <div className="brut-card" data-testid="requisitos-loading">
        <div className="label-mono text-muted-foreground">Requisitos Locales</div>
        <div className="mt-2 text-sm text-muted-foreground">Cargando…</div>
      </div>
    );
  }
  if (!data) return null;

  const flag = data.country ? COUNTRY_FLAGS[data.country] || "" : "";
  const pending = data.items.filter((it) => !it.completed);
  const completed = data.items.filter((it) => it.completed);

  // Compact variant — used on Hoy to surface the pending count.
  if (variant === "compact") {
    if (data.early && data.items.length === 0) return null;
    if (pending.length === 0 && completed.length === 0) return null;
    return (
      <a
        href="#requisitos-locales"
        className="brut-cta hover:translate-x-[-1px] hover:translate-y-[-1px] block"
        data-testid="requisitos-compact-card"
        onClick={(e) => {
          // Smooth scroll if target exists on same page (PlaybookDetail),
          // otherwise let the browser resolve — Hoy links back to active playbook.
          const el = document.getElementById("requisitos-locales");
          if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-mono text-muted-foreground flex items-center gap-1">
              <MapPin size={12} weight="bold" /> Requisitos Locales
            </div>
            <div className="mt-1 text-lg font-semibold leading-tight">
              {pending.length} pendiente{pending.length === 1 ? "" : "s"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {flag} {data.city || data.country || ""}
            </div>
          </div>
          <div className="display text-3xl leading-none">{pending.length}</div>
        </div>
      </a>
    );
  }

  // Full variant — rendered inside PlaybookDetail.
  return (
    <section id="requisitos-locales" className="brut-card" data-testid="requisitos-locales">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-mono text-muted-foreground flex items-center gap-1.5">
            <MapPin size={12} weight="bold" /> Requisitos Locales
          </div>
          <h3 className="display text-2xl sm:text-3xl mt-1 leading-tight">
            {flag} {[data.city, data.country].filter(Boolean).join(", ") || "Ubicación por definir"}
          </h3>
          {!data.has_dedicated_catalog && data.country && (
            <p className="mt-2 text-xs text-muted-foreground max-w-md leading-relaxed">
              Aún estamos construyendo el catálogo específico para {data.country}. Mientras tanto,
              te mostramos un marco genérico basado en los requisitos comunes.
            </p>
          )}
        </div>
        {data.items.length > 0 && (
          <div className="brut-card !p-2 !border-2 text-center">
            <div className="label-mono text-muted-foreground">Pend.</div>
            <div className="display text-2xl leading-none">{pending.length}</div>
          </div>
        )}
      </header>

      {data.early && (
        <div
          className="mt-4 border-2 border-dashed border-foreground p-4 flex items-start gap-3"
          data-testid="requisitos-early-state"
        >
          <Info size={20} weight="bold" className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Todavía es pronto</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{data.reason}</p>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              Sugerencia: si ya vendes o ya contratas, actualiza tu estado en <strong>Mi Negocio</strong>{" "}
              para ver los requisitos que te aplican.
            </p>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-4" data-testid="requisitos-pendientes">
          <div className="label-mono text-muted-foreground mb-2">Pendientes ({pending.length})</div>
          <ul className="space-y-2">
            {pending.map((it) => (
              <RequisitoRow key={it.id} item={it} onToggle={toggle} saving={saving[it.id]} />
            ))}
          </ul>
        </div>
      )}

      {completed.length > 0 && (
        <div className="mt-4" data-testid="requisitos-completados">
          <button
            type="button"
            className="label-mono text-muted-foreground flex items-center gap-1 hover:text-foreground"
            onClick={() => setShowCompleted((v) => !v)}
            data-testid="btn-toggle-completados"
          >
            Completados ({completed.length})
            {showCompleted ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          </button>
          {showCompleted && (
            <ul className="space-y-2 mt-2">
              {completed.map((it) => (
                <RequisitoRow key={it.id} item={it} onToggle={toggle} saving={saving[it.id]} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function RequisitoRow({ item, onToggle, saving }) {
  const Icon = item.completed ? CheckCircle : Circle;
  return (
    <li
      className={`border-2 border-foreground p-3 flex items-start gap-3 ${item.completed ? "bg-surface opacity-70" : "bg-surface"}`}
      data-testid={`requisito-${item.id}`}
      data-completed={item.completed ? "true" : "false"}
    >
      <button
        type="button"
        aria-label={item.completed ? "Marcar como pendiente" : "Marcar como completado"}
        onClick={() => onToggle(item)}
        disabled={saving}
        className={`flex-shrink-0 mt-0.5 transition-transform hover:scale-110 ${saving ? "opacity-50" : ""}`}
        data-testid={`toggle-${item.id}`}
      >
        <Icon size={22} weight={item.completed ? "fill" : "regular"} />
      </button>
      <div className="min-w-0 flex-1">
        <div className={`font-semibold text-sm leading-snug ${item.completed ? "line-through opacity-75" : ""}`}>
          {item.title}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span className="label-mono text-[0.65rem] text-muted-foreground inline-flex items-center gap-1">
            <Buildings size={10} weight="bold" /> {item.authority}
          </span>
          <span className="label-mono text-[0.65rem] text-muted-foreground">· {item.category_label}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{item.description}</p>
        {item.help_url && (
          <a
            href={item.help_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline decoration-2 underline-offset-2 hover:opacity-80"
            data-testid={`help-${item.id}`}
          >
            Ver oficial <ArrowSquareOut size={10} weight="bold" />
          </a>
        )}
      </div>
    </li>
  );
}
